import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';


// `namespace`, when given, suffixes every param's actual URL query key
// (e.g. "sortBy" -> "sortBy-worktray-0") so multiple instances of the same
// widget on one page (e.g. two WorkTrays in a Workpage) each read/write their
// own slice of the query string instead of fighting over the same key.
// Callers still read/write using the plain param name in `params` — only the
// key that hits the URL changes. Omitting it preserves the original,
// unnamespaced behavior for every other caller.
function useSearchParams(params, namespace){
  const { search, pathname } = useLocation();
  const history = useHistory();
  const parsed = useMemo(() => queryString.parse(search || ''), [search]);

  const historyRef = useRef();
  historyRef.current = history;
  const parsedRef = useRef();
  parsedRef.current = parsed;

  const keyFor = useCallback((param) => (namespace ? `${param}-${namespace}` : param), [namespace]);

  const setQueryString = useCallback((pathname, qsparams) => {
    historyRef.current.push(`${pathname}?${queryString.stringify(qsparams)}`);
  }, [historyRef]);

  const setParam = useCallback((param, value) => {
    const realKey = keyFor(param);
    parsedRef.current = Object.entries({...(parsedRef.current || {}), [realKey]: value}).reduce((_, [k, v]) => {
      if (v !== undefined) _[k] = v;

      return _;
    }, {});
    setQueryString(pathname, parsedRef.current);
  }, [pathname, keyFor]);

  const paramList = useMemo(() => Object.keys(params), [Object.keys(params).join(',')]);

  const setters = useMemo(() => [
    ...paramList.map(param => (
      (value) => setParam(param, value)
    )),
    (newparams) => {
      parsedRef.current = {...newparams};
      setQueryString(pathname, parsedRef.current);
    }
  ], [pathname, setParam, paramList]);

  return [
    ...paramList.map((param, idx) => [
      parsedRef.current[keyFor(param)] || params[param],
      setters[idx]
    ]),
    [
      parsedRef.current,
      setters[setters.length - 1]
    ]
  ];
}


export default useSearchParams;